import React, { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { StarIcon as StarSolidIcon, AcademicCapIcon, ClockIcon, BookOpenIcon, UserGroupIcon, CalendarIcon, CheckBadgeIcon } from '@heroicons/react/24/solid'
import { StarIcon, ChatBubbleLeftIcon, MapPinIcon, GlobeAltIcon, PlayIcon, ArrowRightIcon, SparklesIcon, ShieldCheckIcon } from '@heroicons/react/24/outline'
import { toast } from 'react-hot-toast'

interface Teacher {
  id: string
  title: string
  introduction: string
  specialties: string[]
  rating: number
  status: string
  bio: string
  experience: string
  education: string
  phoneNumber?: string
  highestDegree?: string
  graduatedUniversity?: string
  graduationYear?: number
  major?: string
  teachingExperience?: string
  createdAt: string
  user: {
    firstName: string
    lastName: string
    avatar?: string
    email: string
  }
  courses?: Course[]
}

interface Course {
  id: string
  title: string
  description: string
  subject: string
  finalPricePerHour?: string
  price: string
  duration: number
  status: string
  _count?: {
    bookings: number
  }
}

const TeacherDetail = () => {
  const { id } = useParams<{ id: string }>()
  const [teacher, setTeacher] = useState<Teacher | null>(null)
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('overview')
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const navigate = useNavigate()

  useEffect(() => {
    if (id) {
      fetchTeacher(id)
    }
    checkAuth()
  }, [id])

  const checkAuth = () => {
    const token = localStorage.getItem('token')
    setIsAuthenticated(!!token)
  }

  const fetchTeacher = async (teacherId: string) => {
    try {
      const response = await fetch(`/api/teachers/${teacherId}`)
      if (response.ok) {
        const data = await response.json()
        setTeacher(data)
      } else {
        console.error('获取教师详情失败')
      }
    } catch (error) {
      console.error('获取教师详情失败:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleContact = () => {
    if (!isAuthenticated) {
      toast.error('请先登录')
      return
    }
    
    // 跳转到该教师的课程列表页面
    navigate(`/courses?teacher=${teacher.id}`)
    toast.success('正在跳转到课程页面...')
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
          {/* 骨架屏 */}
          <div className="animate-pulse">
            <div className="h-64 bg-gradient-to-r from-purple-100 to-indigo-100 rounded-3xl mb-8"></div>
            <div className="grid lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2 space-y-6">
                <div className="bg-white rounded-2xl p-6 h-48"></div>
                <div className="bg-white rounded-2xl p-6 h-64"></div>
              </div>
              <div className="bg-white rounded-2xl p-6 h-80"></div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!teacher) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center">
        <div className="text-center py-20 px-8">
          <div className="w-24 h-24 mx-auto mb-6 rounded-3xl bg-gradient-to-br from-purple-100 to-indigo-100 flex items-center justify-center">
            <UserGroupIcon className="w-12 h-12 text-purple-400" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-3">导师信息未找到</h1>
          <p className="text-gray-500 mb-6">该导师可能已不再活跃或信息有误</p>
          <Link
            to="/teachers"
            className="inline-flex items-center px-6 py-3 text-sm font-semibold rounded-xl text-white bg-gradient-to-r from-purple-600 to-indigo-600 hover:shadow-lg hover:shadow-purple-500/25 transition-all"
          >
            <ArrowRightIcon className="w-4 h-4 mr-2 rotate-180" />
            返回导师列表
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* 高级Hero区域 */}
      <div className="relative bg-gradient-to-br from-purple-600 via-indigo-600 to-blue-700 overflow-hidden">
        {/* 背景装饰 */}
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
          <div className="absolute bottom-0 left-0 w-80 h-80 bg-purple-400/20 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>
          <div className="absolute inset-0 opacity-40" style={{backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.15) 1px, transparent 1px)', backgroundSize: '24px 24px'}}></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          {/* 面包屑导航 */}
          <nav className="flex mb-8" aria-label="Breadcrumb">
            <ol className="inline-flex items-center space-x-2 text-sm">
              <li><Link to="/" className="text-white/60 hover:text-white transition-colors">首页</Link></li>
              <li className="text-white/40">/</li>
              <li><Link to="/teachers" className="text-white/60 hover:text-white transition-colors">导师</Link></li>
              <li className="text-white/40">/</li>
              <li className="text-white font-medium">{teacher.user.firstName} {teacher.user.lastName}</li>
            </ol>
          </nav>

          <div className="lg:flex lg:items-center lg:gap-12">
            {/* 头像区域 */}
            <div className="flex-shrink-0 mb-8 lg:mb-0">
              <div className="relative">
                <div className="w-36 h-36 lg:w-44 lg:h-44 rounded-3xl bg-white/10 backdrop-blur-sm p-1.5 shadow-2xl">
                  <div className="w-full h-full rounded-2xl bg-gradient-to-br from-purple-100 to-indigo-100 flex items-center justify-center overflow-hidden">
                    {teacher.user.avatar ? (
                      <img
                        className="w-full h-full rounded-2xl object-cover"
                        src={teacher.user.avatar}
                        alt={`${teacher.user.firstName} ${teacher.user.lastName}`}
                      />
                    ) : (
                      <span className="text-5xl font-bold text-purple-600">
                        {teacher.user.firstName.charAt(0)}{teacher.user.lastName.charAt(0)}
                      </span>
                    )}
                  </div>
                </div>
                {/* 认证徽章 */}
                <div className="absolute -bottom-2 -right-2 w-10 h-10 bg-green-500 rounded-xl flex items-center justify-center border-4 border-white shadow-lg">
                  <CheckBadgeIcon className="w-6 h-6 text-white" />
                </div>
              </div>
            </div>

            {/* 基本信息 */}
            <div className="flex-1 text-center lg:text-left">
              <div className="flex items-center justify-center lg:justify-start gap-3 mb-3">
                <h1 className="text-3xl lg:text-4xl font-bold text-white">
                  {teacher.user.firstName} {teacher.user.lastName}
                </h1>
                <span className="px-3 py-1 rounded-full bg-white/20 backdrop-blur-sm text-white text-sm font-medium">
                  认证导师
                </span>
              </div>
              <p className="text-xl text-white/80 mb-4">{teacher.title}</p>

              {/* 评分 */}
              <div className="flex items-center justify-center lg:justify-start gap-4 mb-6">
                <div className="flex items-center gap-1">
                  {[0, 1, 2, 3, 4].map((rating) => (
                    <StarSolidIcon
                      key={rating}
                      className={`${
                        (parseFloat(String(teacher.rating)) || 0) > rating ? 'text-amber-400' : 'text-white/30'
                      } h-5 w-5 flex-shrink-0`}
                    />
                  ))}
                  <span className="ml-2 text-white font-semibold">
                    {(parseFloat(String(teacher.rating)) || 0).toFixed(1)}
                  </span>
                </div>
                <span className="text-white/60">|</span>
                <span className="text-white/80">{teacher.courses?.length || 0} 门课程</span>
              </div>

              {/* 专长标签 */}
              <div className="flex flex-wrap gap-2 justify-center lg:justify-start mb-8">
                {teacher.specialties.slice(0, 5).map((specialty, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium bg-white/15 backdrop-blur-sm text-white border border-white/20"
                  >
                    {specialty}
                  </span>
                ))}
              </div>

              {/* 操作按钮 */}
              <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start">
                <button
                  onClick={handleContact}
                  className="inline-flex items-center justify-center px-8 py-3.5 text-base font-semibold rounded-xl text-purple-700 bg-white hover:bg-purple-50 shadow-lg hover:shadow-xl transition-all"
                >
                  <ChatBubbleLeftIcon className="h-5 w-5 mr-2" />
                  联系导师
                </button>
                <Link
                  to={`/courses?teacher=${teacher.id}`}
                  className="inline-flex items-center justify-center px-8 py-3.5 text-base font-semibold rounded-xl text-white bg-white/15 backdrop-blur-sm border border-white/30 hover:bg-white/25 transition-all"
                >
                  <BookOpenIcon className="h-5 w-5 mr-2" />
                  查看课程
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 统计卡片区域 */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-12 relative z-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: '开设课程', value: teacher.courses?.length || 0, icon: BookOpenIcon, color: 'from-blue-500 to-cyan-500' },
            { label: '学生总数', value: teacher.courses?.reduce((sum, course) => sum + (course._count?.bookings || 0), 0) || 0, icon: UserGroupIcon, color: 'from-purple-500 to-pink-500' },
            { label: '教学经验', value: teacher.experience || '丰富', icon: ClockIcon, color: 'from-amber-500 to-orange-500' },
            { label: '综合评分', value: (parseFloat(String(teacher.rating)) || 0).toFixed(1), icon: StarSolidIcon, color: 'from-emerald-500 to-teal-500' }
          ].map((stat, i) => (
            <div key={i} className="bg-white rounded-2xl p-5 shadow-lg border border-gray-100 hover:shadow-xl transition-all group">
              <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center mb-3 group-hover:scale-110 transition-transform`}>
                <stat.icon className="w-5 h-5 text-white" />
              </div>
              <div className="text-2xl font-bold text-gray-900">{stat.value}</div>
              <div className="text-sm text-gray-500">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* 主要内容区域 */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* 标签页 */}
        <div className="bg-white shadow-lg rounded-2xl border border-gray-100 overflow-hidden">
          <div className="border-b border-gray-100 px-6 pt-2">
            <nav className="flex space-x-1" aria-label="Tabs">
              {[
                { id: 'overview', label: '个人简介' },
                { id: 'courses', label: '开设课程' },
                { id: 'education', label: '教育背景' }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`relative px-6 py-4 text-sm font-medium transition-all ${
                    activeTab === tab.id
                      ? 'text-purple-600'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {tab.label}
                  {activeTab === tab.id && (
                    <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full"></span>
                  )}
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6 lg:p-8">
            {/* 个人简介标签页 */}
            {activeTab === 'overview' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-3">个人简介</h3>
                  <p className="text-gray-600 leading-relaxed">
                    {teacher.bio || teacher.introduction}
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-3">专业特长</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {teacher.specialties.map((specialty, index) => (
                      <div key={index} className="flex items-center">
                        <span className="w-2 h-2 bg-blue-400 rounded-full mr-3"></span>
                        <span className="text-gray-600">{specialty}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-3">教学理念</h3>
                  <p className="text-gray-600 leading-relaxed">
                    秉承因材施教的教学理念，根据每个学生的特点制定个性化的学习计划。
                    注重培养学生的学习兴趣和自主学习能力，让学习变得轻松愉快。
                    通过丰富的教学经验和专业知识，帮助学生在学习中取得优异成绩。
                  </p>
                </div>
              </div>
            )}

            {/* 开设课程标签页 */}
            {activeTab === 'courses' && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-6">开设课程</h3>
                {teacher.courses && teacher.courses.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {teacher.courses.filter(course => course.status === 'approved').map((course) => (
                      <div key={course.id} className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                        <div className="flex items-center justify-between mb-3">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {course.subject}
                          </span>
                          <div>
                            {/* 判断是否有折扣 */}
                            {course.finalPricePerHour && course.price && parseFloat(course.price) < parseFloat(course.finalPricePerHour) ? (
                              // 有折扣的情况：显示折后价格和原价
                              <div className="text-right">
                                <div className="text-lg font-bold text-red-600">
                                  ¥{course.price}/小时
                                </div>
                                <div className="text-xs text-gray-500 line-through">
                                  原价: ¥{course.finalPricePerHour}
                                </div>
                              </div>
                            ) : (
                              // 没有折扣的情况：只显示最终价格
                              <div className="text-lg font-bold text-gray-900">
                                ¥{course.finalPricePerHour || course.price}/小时
                              </div>
                            )}
                          </div>
                        </div>
                        
                        <h4 className="text-lg font-medium text-gray-900 mb-2">{course.title}</h4>
                        <p className="text-gray-600 text-sm mb-4 line-clamp-2">{course.description}</p>
                        
                        <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                          <div className="flex items-center">
                            <ClockIcon className="h-3 w-3 mr-1" />
                            {course.duration} 小时
                          </div>
                          <div className="flex items-center">
                            <UserGroupIcon className="h-3 w-3 mr-1" />
                            {course._count?.bookings || 0} 人已预约
                          </div>
                        </div>
                        
                        <Link
                          to={`/courses/${course.id}`}
                          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-blue-600 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                          查看详情
                        </Link>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <BookOpenIcon className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">暂无课程</h3>
                    <p className="mt-1 text-sm text-gray-500">该教师暂未开设任何课程</p>
                  </div>
                )}
              </div>
            )}

            {/* 教育背景标签页 */}
            {activeTab === 'education' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">教育背景</h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <dt className="text-sm font-medium text-gray-500">最高学历</dt>
                        <dd className="text-sm text-gray-900">{teacher.highestDegree || '学士学位'}</dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">毕业院校</dt>
                        <dd className="text-sm text-gray-900">{teacher.graduatedUniversity || teacher.education}</dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">专业</dt>
                        <dd className="text-sm text-gray-900">{teacher.major || '相关专业'}</dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">毕业年份</dt>
                        <dd className="text-sm text-gray-900">{teacher.graduationYear || '2020年'}</dd>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">教学经验</h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-600">
                      {teacher.teachingExperience || `拥有${teacher.experience}的丰富教学经验，曾在多所知名教育机构任教，具有深厚的专业功底和丰富的教学实践经验。`}
                    </p>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">认证信息</h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center text-sm text-gray-600">
                      <span className="w-2 h-2 bg-green-400 rounded-full mr-3"></span>
                      已通过平台身份认证
                    </div>
                    <div className="flex items-center text-sm text-gray-600 mt-2">
                      <span className="w-2 h-2 bg-green-400 rounded-full mr-3"></span>
                      已通过教学资质认证
                    </div>
                    <div className="flex items-center text-sm text-gray-600 mt-2">
                      <span className="w-2 h-2 bg-green-400 rounded-full mr-3"></span>
                      已通过专业技能认证
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* 联系卡片 */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <ChatBubbleLeftIcon className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-3 flex-1">
              <h3 className="text-lg font-medium text-blue-900">联系 {teacher.user.firstName} 老师</h3>
              <p className="mt-2 text-sm text-blue-700">
                如果您对老师的课程感兴趣或有任何疑问，可以直接联系老师了解更多详情。
              </p>
              <div className="mt-4">
                <button
                  onClick={handleContact}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  发送消息
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default TeacherDetail 